import { MasterdataService } from './../../shared/generated/backend/api/masterdata.service';
import { ValueChangedEvent as CheckBoxValueChangedEvent } from 'devextreme/ui/check_box_types';
import {
  Component,
  WritableSignal,
  signal,
  computed,
  OnInit,
  HostListener,
  OnDestroy,
  inject,
  input,
  output,
} from '@angular/core';
import {
  DxDraggableModule,
  DxDropDownButtonModule,
  DxListModule,
  DxTabPanelModule,
  DxTileViewModule,
  DxToolbarModule,
  DxCheckBoxModule,
  DxTooltipModule,
  DxLoadIndicatorModule,
} from 'devextreme-angular';
import { PlanningDialogComponent } from './dialogs/planning-dialog/planning-dialog.component';
import { LoadunitSelectionDialogComponent } from './dialogs/loadunit-selection-dialog/loadunit-selection-dialog.component';
import { PlStationSelectionDialogComponent } from './dialogs/pl-station-selection-dialog/pl-station-selection-dialog.component';
import { AssignmentStatusDialogComponent } from './dialogs/assignment-status-dialog/assignment-status-dialog.component';
import { DxSortableModule } from 'devextreme-angular/ui/sortable';
import {
  AssignmentType,
  AssignmentDto,
  AssignmentService,
  LoadunitDto,
  ProjectService,
  PlanningService,
  PlanningRequestDto,
  PlanningResponseDto,
  SwapLoadUnitRequestDto,
} from '../../shared/generated/backend';
import { AssignmentSubtableDataDto } from '../../shared/generated/backend/model/assignmentSubtableDataDto';
import { DragEndEvent } from 'devextreme/ui/sortable';
import {
  AddProjectLoadunitDto,
  AssignmentCompletionDto,
  AssignmentCompletionOption,
  LoadunitType,
  ProcessingState,
  ProjectDto,
  ProjectLoadunitDto,
} from '../../shared/generated/backend';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import { ContentReadyEvent, ItemClickEvent, ItemReorderedEvent } from 'devextreme/ui/list';
import notify from 'devextreme/ui/notify';
import { ContextService } from '../../services/context.service';
import { DxoLabelModule } from 'devextreme-angular/ui/nested';
import { PlanningLoadunit, PlanningLoadunits } from '../../shared/planning/PlanningLoadunit';
import { CargoLoad, LoadUnitSlot } from '../../shared/planning/loadunitSlot';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { PlanningHelperService } from '../../services/planning-helper.service';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { assignmentNoteChangedMap, assignmentNoteStateNoMap } from '../../shared/planning/assignmentNote';
import { MapService } from '../../services/map.service';
import { LoadUnitSlots } from './loadUnitSlots';
import { LoaderComponent } from '../../loader/loader.component';
import {
  BadgeVariantComponent,
  Shape,
} from '../../shared/components/badge-variant/badge-variant/badge-variant.component';
import { showEventTime } from '../../shared/util/assignment-extended';
import { createBusyState, createKeyedBusyState } from '../../shared/util/busy-state';

export enum PlanningMode {
  AssignmentPlanning,
  SwapLoadUnit,
  ChangeLoadUnit,
}

interface Note {
  note: string;
  parameters?: Record<string, string>;
}

class LoadunitUserSelection {
  loadunitId: number;
  isRestBleibt: boolean;

  constructor(loadunitId: number, isRestBleibt: boolean) {
    this.loadunitId = loadunitId;
    this.isRestBleibt = isRestBleibt;
  }
}

@Component({
  selector: 'app-project-planning',
  imports: [
    SharedModule,
    CommonModule,
    DxListModule,
    DxSortableModule,
    DxTooltipModule,
    DxDraggableModule,
    DxTileViewModule,
    DxDropDownButtonModule,
    DxTabPanelModule,
    DxoLabelModule,
    IconComponent,
    LoaderComponent,
    DxToolbarModule,
    DxCheckBoxModule,
    DxLoadIndicatorModule,
    BadgeVariantComponent,
    PlanningDialogComponent,
    LoadunitSelectionDialogComponent,
    PlStationSelectionDialogComponent,
    AssignmentStatusDialogComponent,
  ],
  templateUrl: './project-planning.component.html',
  styleUrl: './project-planning.component.scss',
})
export class ProjectPlanningComponent implements OnInit, OnDestroy {
  readonly project = input.required<WritableSignal<ProjectDto>>();
  readonly reloadDataRequired = output<void>();
  readonly Shape = Shape;

  private projectService = inject(ProjectService);
  private assignmentService = inject(AssignmentService);
  private masterdataService = inject(MasterdataService);
  private contextService = inject(ContextService);
  private planningHelperService = inject(PlanningHelperService);
  private planningService = inject(PlanningService);
  private translateService = inject(TranslateService);
  private mapService = inject(MapService);

  private onUserLoadunitSelectionAvailable = new Subject<LoadunitUserSelection>();
  private onUserPlStationSelectionAvailable = new Subject<number[] | null>();
  private plStationSelectionResolved = false;

  projectLoadunits: WritableSignal<ProjectLoadunitDto[]> = signal([]);
  mainPrimeLogisticLoadUnits: WritableSignal<ProjectLoadunitDto[]> = signal([]);
  planningAllowed: boolean = false;
  readonly planningExecution = createBusyState();
  isLoading: boolean = true;
  // Guards deleteProjectLoadUnit's own (possibly multi-row, sequential) cascade against
  // a double-click or a second delete started on another row before the first finishes.
  // Tracks the Id of the row actually being deleted so only that row shows progress.
  readonly deletingRows = createKeyedBusyState<number>();
  // Guards start/finish edit of the project.
  readonly editBusy = createBusyState();
  // Guards multi-row updates (reordering, changing the loadunit of a slot) against overlapping runs.
  readonly loadunitUpdate = createBusyState();
  // Guards per-row actions (confirming a note, completing/reopening an assignment).
  readonly rowBusy = createKeyedBusyState<number>();
  // Bumped on every fetchProjectLoadunits() call so an in-flight, superseded request can
  // detect it's no longer the latest and skip applying its (possibly stale) result.
  private fetchProjectLoadunitsGeneration = 0;

  // TruckAndTrailer<loadunitSlot> = new TruckAndTrailer<loadunitSlot>;
  loadUnitSlots: LoadUnitSlots = new LoadUnitSlots(); // loadunitSlot[] = [];
  selectedLoadUnitSlot: LoadUnitSlot | null = null; // the selected loadunit slot

  availableLoadunits: LoadunitDto[] = []; // all available loadunits
  loadunitSelection: LoadunitDto[] = []; // all selectable loadunits (available - used)
  loadUnitSlotSelectionOrder: number[] = []; // the order in which the loadunit slots were selected by the user
  planningMode: PlanningMode = PlanningMode.AssignmentPlanning; // the current planning mode
  selectedLoadunitId: number = 0;
  planAssignmentSubscription = new Subscription();
  placeholders: unknown[] = [];

  // PlStation selection
  showPlStationSelectionDialog: boolean = false;
  currentPlAssignment: AssignmentDto | null = null;
  currentPlSubtableData: AssignmentSubtableDataDto | null = null;
  selectedPlStations: number[] = [];

  get selectedLoadunit(): LoadunitDto | undefined {
    return this.loadunitSelection.find(x => x.id === this.selectedLoadunitId);
  }

  selectedPosition: 'truck' | 'trailer' = 'truck';
  currentPlanning?: PlanningLoadunits; // the current planning object

  projectAdrPointsTotal(): number {
    // Dedupe by AssignmentNo across BOTH load units, not per load unit then summed - an
    // assignment split across truck and trailer must still only count once project-wide.
    const allRows = this.loadUnitSlots.flatMap(lu => lu?.cargoLoad ?? []);
    return CargoLoad.sumUniqueAdrPoints(allRows);
  }

  // list controls
  expanded: boolean = true;
  prevEvent?: ItemClickEvent;
  clickTimeout: number = 400;

  // dialog controls
  showPlanningDialog: boolean = false;
  showChangeLoadunitDialog: boolean = false;
  showAssignmentStatusDialog: boolean = false;

  assignmentCompleteOptions: { display: string; value: AssignmentCompletionOption }[] = Object.values(
    AssignmentCompletionOption
  ).map(value => ({
    value: value,
    display: this.translateService.instant(`ASSIGNMENT_COMPLETION_OPTION.${value}`),
  }));
  assignmentCompleteData: AssignmentCompletionDto = {
    ProjectLoadunitId: -1,
    Completed: true,
    CompletionOption: AssignmentCompletionOption.Good,
    Subject: '',
    Comment: '',
    CompletionOptionsList: [],
  };
  numberOfLoadUnitsSelected: number = 1;
  readonly AssignmentType = AssignmentType;

  /** Stable cache so the template never sees a new array reference for the same loadunit. */
  private readonly _notesCache = new Map<number, Note[]>();
  readonly notesFor = (lu: ProjectLoadunitDto): Note[] => this._notesCache.get(lu.Sorting!) ?? [];

  async ngOnInit() {
    // make sure the assignment-list can call use when it wants to add assignments to this project
    this.planAssignmentSubscription = this.planningHelperService.planAssignment$.subscribe(
      (assignments: AssignmentDto[]) => {
        const insertionIndex = this.projectLoadunits().length + 1;
        console.log('planAssignment$', assignments, insertionIndex);
        this.planAssignments(assignments, insertionIndex);
      }
    );

    await this.fetchProjectLoadunits();
  }

  ngOnDestroy() {
    this.planAssignmentSubscription.unsubscribe();
  }

  get inAssignmentPlanningMode() {
    return this.planningMode == PlanningMode.AssignmentPlanning;
  }

  // returns if the project is in a state where planning is allowed without further checks
  dispoPlanning() {
    return this.project()()?.ProcessingState == ProcessingState.TransportationScheduling;
  }

  // returns if the project is in a state where planning is allowed with further checks
  isPlanningAllowed() {
    return (
      !this.isLoading &&
      !this.planningHelperService.planningInProgress &&
      !this.deletingRows.anyBusy() &&
      !this.loadunitUpdate.busy() &&
      (this.planningAllowed || this.dispoPlanning())
    );
  }

  allowEditPlanning() {
    return !this.dispoPlanning() && this.project()().ProcessingState == ProcessingState.Rolling;
  }

  allowAssignmentStatusChange = () => {
    return this.planningAllowed && this.project()()?.ProcessingState == ProcessingState.Rolling;
  };

  // Hauptauftrag der Prime Logistic erkennen
  isPrimeLogisticMain(loadunit: ProjectLoadunitDto): boolean {
    return loadunit.IsPrimeLogistic === true && loadunit.IsPrimeLogisticStation === false;
  }

  isPrimeLogisticStation(loadunit: ProjectLoadunitDto): boolean {
    return loadunit.IsPrimeLogistic === true && loadunit.IsPrimeLogisticStation === true;
  }

  hasHeaderIcon(loadunit: ProjectLoadunitDto): boolean {
    return (
      this.isPrimeLogisticMain(loadunit) ||
      this.isPrimeLogisticStation(loadunit) ||
      loadunit.LoadunitState === '1' ||
      (!!loadunit.AdrPoints && loadunit.AdrPoints > 0)
    );
  }

  private primeLogisticColorIndexFor(loadunit: ProjectLoadunitDto): number {
    // Colour by the group's main assignment number, so all rows of the same PL
    // assignment (main + its stations, across all load units) share one colour and
    // distinct PL assignments get distinct colours.
    const mainAssignmentNo = this.isPrimeLogisticMain(loadunit) ? loadunit.AssignmentNo : loadunit.ParentAssignmentNo;

    const distinctMainAssignmentNos = Array.from(
      new Set(
        this.mainPrimeLogisticLoadUnits()
          .map(pl => pl.AssignmentNo)
          .filter((no): no is number => no != null)
      )
    );

    return distinctMainAssignmentNos.findIndex(no => no === mainAssignmentNo);
  }

  primeLogisticColorFor(loadunit: ProjectLoadunitDto): string {
    const plColors = [
      'rgb(243, 158, 243)', // pink
      'rgb(158, 205, 243)', // blue
      'rgb(183, 243, 158)', // green
      'rgb(243, 219, 158)', // orange
      'rgb(200, 183, 243)', // purple
      'rgb(243, 183, 183)', // red
    ];
    const index = this.primeLogisticColorIndexFor(loadunit);
    return index >= 0 ? plColors[index % plColors.length] : '';
  }

  projectPlanningListLoadunits(): ProjectLoadunitDto[] {
    return this.projectLoadunits().filter(loadunit => !this.isPrimeLogisticMain(loadunit));
  }

  /**
   * Loadunits to display in the planning list. A PrimeLogistics main assignment 'm'
   * can appear in multiple load units of a project (one ProjectLoadunit row per load
   * unit). All rows are shown (each with its own load unit's load); main PL rows are
   * always displayed on top of the list.
   *
   * Implemented as a computed signal so the array reference stays stable across change
   * detection cycles (recomputing only when projectLoadunits changes). A method would
   * return a new array on every render, which breaks the dx-list item rendering and
   * click/delete handlers.
   */
  readonly displayProjectLoadunits = computed<ProjectLoadunitDto[]>(() => {
    // Stable sort: main PL assignments first, everything else keeps its order.
    return this.projectLoadunits()
      .map((loadunit, index) => ({ loadunit, index }))
      .sort((a, b) => {
        const aMain = this.isPrimeLogisticMain(a.loadunit) ? 0 : 1;
        const bMain = this.isPrimeLogisticMain(b.loadunit) ? 0 : 1;
        return aMain - bMain || a.index - b.index;
      })
      .map(entry => entry.loadunit);
  });

  // allows to modify a project after transportation scheduling
  // after editing a project, the project is locked again and the driver will be notified
  editProject = async () => {
    await this.editBusy.run(async () => {
      try {
        await firstValueFrom(this.projectService.editProject(this.project()().ProjectNo, true));
        // before onload we also ensure the edit is finished
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.planningAllowed = true;
      } catch (error) {
        console.error('Error during startEdit:', error);
        notify(
          { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.CANNOT_MODIFY'), shading: false },
          'error',
          3000
        );
      }
    });
  };

  public async onSwapLoadForClick(projectLoadUnit: ProjectLoadunitDto) {
    if (!this.isPlanningAllowed()) {
      notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.CANNOT_MODIFY'), 'warning');
      return;
    }

    try {
      this.planningMode = PlanningMode.SwapLoadUnit;
      this.isLoading = true;
      this.planningHelperService.planningInProgress = true;

      let requestDto: SwapLoadUnitRequestDto = {};

      if (this.loadUnitSlots.length == 1) {
        // there is only one loadunit -> open the loadunit selection dialog
        const userSelection = await this.openLoadunitSelection();

        requestDto = {
          ToLoadUnit: userSelection.loadunitId,
        };
      }
      await firstValueFrom(
        this.planningService.swapProjectLoadunit(this.project()().ProjectNo, projectLoadUnit.Sorting!, requestDto)
      );
    } catch (error) {
      console.error('Error during onSwapLoadForClick:', error);
      notify({ message: this.translateService.instant('ERROR.Unknown'), shading: false }, 'error', 3000);
    } finally {
      this.planningHelperService.planningInProgress = false;
      // Refresh (and only then announce the data change) before notifying other views -
      // otherwise a listener (e.g. project detail) reloads against the pre-swap state.
      await this.fetchProjectLoadunits();
      this.checkDataChanged();
      this.isLoading = false;
    }
  }

  finishEdit = async () => {
    if (!this.planningAllowed)
      // Nothing was changed
      return;
    await this.editBusy.run(async () => {
      try {
        await firstValueFrom(this.projectService.editProject(this.project()().ProjectNo, false));
        this.planningAllowed = false;
        window.removeEventListener('beforeunload', this.onBeforeUnload);
      } catch (error) {
        console.error('Error during startEdit:', error);
        notify({ message: this.translateService.instant('ERROR.INVALID'), shading: false }, 'error', 3000);
      }
    });
  };

  // before onload we also ensure the edit is finished
  onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    return '';
  };

  onPopUpHide() {
    this.showAssignmentStatusDialog = false;
  }

  changeAssignmentState(isChecked: boolean, projectLoadunit: ProjectLoadunitDto): void {
    if (isChecked) {
      this.openAssignmentStatusDialog(projectLoadunit);
    } else {
      this.reopenAssignment(projectLoadunit);
    }
  }

  loadAssignment(e: ItemClickEvent) {
    if (!e.event?.timeStamp || !this.prevEvent?.event?.timeStamp) {
      // No valid events
      this.prevEvent = e;
      return;
    }

    if (e.event?.timeStamp < this.prevEvent?.event?.timeStamp + this.clickTimeout) {
      // the click happened in the past [clickTimeout] ms
      if (e.itemData == this.prevEvent.itemData) {
        //the same item was clicked
        this.contextService.openContextView(`assignment/${e.itemData.AssignmentNo}/${e.itemData.Type!}`);
      }
    }
    this.prevEvent = e;
  }

  private async fetchProjectLoadunits() {
    // If another fetchProjectLoadunits() call starts before this one's response arrives,
    // this response is stale (it may reflect the DB state from before that later call's
    // mutation) and must not clobber whatever the newer call applies - regardless of which
    // HTTP response happens to resolve first.
    const generation = ++this.fetchProjectLoadunitsGeneration;
    try {
      this.isLoading = true;
      const project = this.project();
      const allPlus = await firstValueFrom(this.projectService.getProjectLoadunits(project().ProjectNo));
      if (generation !== this.fetchProjectLoadunitsGeneration) {
        return; // superseded by a newer fetchProjectLoadunits() call - discard this stale result
      }
      this.projectLoadunits.set(allPlus); // .filter(plu => !this.isMainPrimeLogisticLoadunit(plu)));
      this.mainPrimeLogisticLoadUnits.set(allPlus.filter(plu => this.isPrimeLogisticMain(plu)));
      this.planningHelperService.requestOmCheck(project().ProjectNo!);

      const loadunitsArray = this.projectLoadunits();
      this._notesCache.clear();
      for (const lu of loadunitsArray) {
        this._notesCache.set(lu.Sorting!, this.getNotesForProjectLoadunit(lu));
      }
      this.mapService.updateMap(loadunitsArray, project().ProjectNo).catch(error => {
        console.error('Error at updating the map', error);
        notify(
          { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.ERROR_MAPS'), shading: false },
          'error',
          3000
        );
      });
      await this.fetchLoadunits();
    } catch (error) {
      if (generation !== this.fetchProjectLoadunitsGeneration) {
        return; // superseded - a newer call is responsible for isLoading/placeholders below
      }
      console.error('Error during getProjectLoadunits:', error);
      notify(
        { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.ERROR_LOADING'), shading: false },
        'error',
        3000
      );
      this.contextService.closeContextView();
    } finally {
      if (generation === this.fetchProjectLoadunitsGeneration) {
        this.isLoading = false;
        this.placeholders = Array.from({
          length: 2 - this.loadUnitSlots.length,
        });
      }
    }
  }

  getNotesForProjectLoadunit(projectLoadunit: ProjectLoadunitDto): Note[] {
    const notes = [];
    if (projectLoadunit.StateNo) {
      const status = assignmentNoteStateNoMap[projectLoadunit.StateNo]?.toUpperCase();
      if (status != null) notes.push({ note: status });
    }
    if (
      (projectLoadunit.CurrentVolume ?? 0) +
        (projectLoadunit.CurrentVolume_B ?? 0) +
        (projectLoadunit.CurrentWeight ?? 0) +
        (projectLoadunit.CurrentWeight_B ?? 0) ===
      0
    ) {
      notes.push({ note: 'NO_CARGO' });
    }
    if (projectLoadunit.ChangeState !== null && projectLoadunit.ChangeState !== undefined) {
      const status = assignmentNoteChangedMap[projectLoadunit.ChangeState]?.toUpperCase();
      if (status != null) notes.push({ note: status });
    }
    return notes;
  }

  private async fetchLoadunits() {
    // get the loadunit selection from the backend
    try {
      this.availableLoadunits = await firstValueFrom(this.masterdataService.getLoadunits());
      // filter out the physical loadunits
      this.availableLoadunits = this.availableLoadunits.sort((a, b) => {
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }); // todo used to be by load weight a.maxLoad - b.maxLoad;
      });
      this.matchLoadunits(); // match and display them in the slots
    } catch (error) {
      console.error('Error during getLoadunits:', error);
      notify({ message: (error as { message: string }).message, shading: false }, 'error', 3000);
    }
  }

  public onAdd = (e: DragEndEvent) => {
    const assignments = e.itemData as AssignmentDto[];
    const insertionIndex = e.toIndex + 1;

    this.planAssignments(assignments, insertionIndex);
  };

  private async planAssignments(assignments: AssignmentDto[], insertionIndex: number): Promise<void> {
    if (!this.isPlanningAllowed()) {
      notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.CANNOT_MODIFY'), 'warning');
      return;
    }
    try {
      this.planningMode = PlanningMode.AssignmentPlanning;
      this.isLoading = true;
      this.planningHelperService.planningInProgress = true;

      // handle prime logistic assignments: sekect station assignments to effectively plan
      let plStationSelection: number[] = [];
      for (const assignment of assignments) {
        if (assignment.IsPrimeLogistic) {
          const subtableData = await firstValueFrom(
            this.assignmentService.getAssignmentSubtableData(assignment.AssignmentNo, assignment.Type)
          );
          const selectedStations = await this.openPlStationSelection(assignment, subtableData);
          if (selectedStations === null) {
            notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'), 'warning');
            return;
          }
          plStationSelection = plStationSelection.concat(selectedStations);
        }
      }

      // initial loadunit selection
      const userSelection = await this.openLoadunitSelection();
      const loadunitSelection = [];

      if (userSelection.isRestBleibt) {
        notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'), 'info');
        return;
      }

      loadunitSelection.push(userSelection.loadunitId);

      const response: PlanningResponseDto = await this.sendExecutePlanningRequest(
        assignments,
        loadunitSelection,
        plStationSelection,
        insertionIndex,
        false
      );

      if (response.Result == PlanningResponseDto.ResultEnum.NeedMoreLoadingUnits) {
        const userSelection2 = await this.openLoadunitSelection();

        if (!userSelection2.isRestBleibt) {
          loadunitSelection.push(userSelection2.loadunitId);
        }

        const response2: PlanningResponseDto = await this.sendExecutePlanningRequest(
          assignments,
          loadunitSelection,
          plStationSelection,
          insertionIndex,
          userSelection2.isRestBleibt
        );

        if (response2.Result == PlanningResponseDto.ResultEnum.Success) {
          this.notifyPlanningCompletion(assignments);

          notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_DONE'), 'success');
          return;
        }
      } else if (response.Result == PlanningResponseDto.ResultEnum.EventDateCheckFailed) {
        notify(this.translateService.instant('ASSIGNMENT.ERROR_VA'), 'error');
        return;
      } else if (response.Result == PlanningResponseDto.ResultEnum.AtLeastOneNonPlannableAssignment) {
        notify(
          this.translateService.instant('ASSIGNMENT.ERROR_NON_PLANNABLE_ASSIGNMENT') +
            (response.AdditionalInfo !== ''
              ? ' ' +
                this.translateService.instant('ASSIGNMENT.ERROR_NON_PLANNABLE_ASSIGNMENT_ADDITIONAL_INFO', {
                  assignments: response.AdditionalInfo,
                })
              : ''),
          'error'
        );
        return;
      } else if (response.Result == PlanningResponseDto.ResultEnum.Success) {
        this.notifyPlanningCompletion(assignments);
        notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_DONE'), 'success');
        return;
      }

      notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'), 'error');
    } catch (error) {
      console.log(error);
      notify(this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'), 'error');
    } finally {
      this.planningHelperService.planningInProgress = false;
      this.isLoading = false;
      await this.fetchProjectLoadunits();
    }
  }

  private async notifyPlanningCompletion(assignmentDtos: AssignmentDto[]) {
    for (const assignmentDto of assignmentDtos) {
      this.planningHelperService.notifyPlanningStepDone(
        {
          AssignmentNo: assignmentDto.AssignmentNo,
          Type: assignmentDto.Type,
        },
        'done'
      );
    }

    this.planningHelperService.notifyPlanningComplete();
    this.checkDataChanged();
  }

  private async sendExecutePlanningRequest(
    assignments: AssignmentDto[],
    loadunitSelection: number[],
    plStationSelection: number[],
    insertionIndex: number,
    isRestBleibt: boolean
  ) {
    const planningRequest: PlanningRequestDto = {
      ProjectNo: this.project()().ProjectNo,
      Assignments: assignments.map(x => ({ AssignmentId: x.AssignmentNo, Type: x.Type })),
      LoadingUnits: loadunitSelection,
      PlStationSelection: plStationSelection,
      InsertPosition: insertionIndex,
      RestRemains: isRestBleibt,
    };

    return await firstValueFrom(this.planningService.executePlanning(planningRequest));
  }

  public numberOfLoadUnits = () => {
    return this.projectLoadunits().length;
  };

  async openLoadunitSelection(): Promise<LoadunitUserSelection> {
    this.selectedLoadUnitSlot = null;
    // Find distinct LoadunitIds from projectLoadunits
    const distinctLoadunitIds = Array.from(
      new Set(
        this.projectLoadunits()
          .map(plu => plu.LoadunitId)
          .concat(this.loadUnitSlots.map(lu => lu?.id))
      )
    );
    this.loadunitSelection =
      distinctLoadunitIds.length == 2
        ? this.availableLoadunits.filter(x => distinctLoadunitIds.includes(x.id))
        : this.availableLoadunits.filter(x => x.type != LoadunitType.PhysicalUnit);

    // first of available loadUnits
    this.selectedLoadunitId = this.loadunitSelection[0].id;

    this.showChangeLoadunitDialog = true;

    return await firstValueFrom(this.onUserLoadunitSelectionAvailable);
  }

  async openPlStationSelection(
    assignment: AssignmentDto,
    subtableData: AssignmentSubtableDataDto
  ): Promise<number[] | null> {
    this.currentPlAssignment = assignment;
    this.currentPlSubtableData = subtableData;
    this.selectedPlStations =
      subtableData.PlStations?.filter(this.isPlStationSelectable).map(station => station.AssignmentNo) || [];

    this.plStationSelectionResolved = false;
    this.showPlStationSelectionDialog = true;

    return await firstValueFrom(this.onUserPlStationSelectionAvailable);
  }

  changeLoadUnitForSlot = (loadUnitSlot: number) => {
    this.planningMode = PlanningMode.ChangeLoadUnit;
    this.selectedLoadUnitSlot = this.loadUnitSlots[loadUnitSlot];

    this.loadunitSelection = this.availableLoadunits.filter(x => x.type != LoadunitType.PhysicalUnit);

    this.showChangeLoadunitDialog = true;
  };

  async onLoadunitSelected() {
    this.showChangeLoadunitDialog = false; // make sure the dialog is closed

    if (this.planningMode == PlanningMode.ChangeLoadUnit) {
      // change load unit of selected slot
      return await this.changeLoadUnitOfSelectedSlot();
    }

    this.onUserLoadunitSelectionAvailable.next(new LoadunitUserSelection(this.selectedLoadunitId, false));
  }

  onPlStationSelected() {
    if (this.plStationSelectionResolved) {
      return;
    }
    this.plStationSelectionResolved = true;
    this.showPlStationSelectionDialog = false;
    this.onUserPlStationSelectionAvailable.next(this.selectedPlStations);
  }

  onPlStationToggle(assignmentNo: number, event: CheckBoxValueChangedEvent) {
    if (event.value) {
      if (!this.selectedPlStations.includes(assignmentNo)) {
        this.selectedPlStations.push(assignmentNo);
      }
    } else {
      this.selectedPlStations = this.selectedPlStations.filter(id => id !== assignmentNo);
    }
  }

  private async changeLoadUnitOfSelectedSlot() {
    await this.loadunitUpdate.run(async () => {
      try {
        if (this.selectedLoadUnitSlot!.loadUnitContainer != this.selectedLoadunit!) {
          this.isLoading = true;
          try {
            for (const plu of this.projectLoadunits().filter(plu => plu.LoadunitId == this.selectedLoadUnitSlot!.id)) {
              plu.LoadunitId = this.selectedLoadunit!.id;
              await firstValueFrom(this.projectService.updateProjectLoadunit(this.project()().ProjectNo, plu));
            }
          } catch (error) {
            // abort on the first failure, the refresh below shows which rows were actually changed
            console.error('Error during changeLoadunit:', error);
            const err = (error as { error: string }).error;
            const errMsg = err != null ? 'ProjectLoadunitError.' + (error as { error: string }).error : 'ERROR.Unknown';
            const message = this.translateService.instant(errMsg);
            notify({ message: message, shading: false }, 'error', 3000);
          }
          await this.fetchProjectLoadunits();
        }
      } finally {
        // reset!
        this.selectedLoadUnitSlot = null;
      }
    });
  }

  canBeConfirmed(note: Note): boolean {
    switch (note.note) {
      case 'CHANGED_ADDRESS':
      case 'CHANGE_UNCRITICAL':
      case 'CHANGED_DELIVERY':
      case 'CHANGED_PICKUP':
      case 'CHANGED_CARGO':
      case 'CHANGED_POSITION':
        return true;
      default:
        return false;
    }
  }

  async confirmNote(note: Note, plu: ProjectLoadunitDto): Promise<void> {
    await this.rowBusy.run(plu.Id!, async () => {
      plu.ChangeState = 0;
      try {
        await firstValueFrom(this.projectService.updateProjectLoadunit(this.project()().ProjectNo, plu));
      } catch (error) {
        console.error('Error during confirmNote:', error);
        const err = (error as { error: string }).error;
        const errMsg = err != null ? 'ProjectLoadunitError.' + (error as { error: string }).error : 'ERROR.Unknown';
        const message = this.translateService.instant(errMsg);
        notify({ message: message, shading: false }, 'error', 3000);
      } finally {
        await this.fetchProjectLoadunits();
      }
    });
  }

  matchLoadunits() {
    // match the loadunits from the projectLoadunits
    const usedLus: LoadunitDto[] = [];
    for (let i = 0; i < this.projectLoadunits().length; i++) {
      const lu = this.availableLoadunits.find(x => x.id == this.projectLoadunits()[i].LoadunitId);
      if (!lu) {
        console.error('loadunit not found', this.projectLoadunits()[i].LoadunitId);
        continue;
      }

      if (!usedLus.find(x => x.id == this.projectLoadunits()[i].LoadunitId)) usedLus.push(lu);
    }
    if (usedLus.length > 2) {
      notify(
        { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.TOOMANY_LOADUNITS'), shading: false },
        'error',
        3000
      );
    }
    this.loadUnitSlots.syncWith(usedLus);
    this.calculateCargoLoad();
  }

  calculateCargoLoad() {
    // reset the cargoLoad
    if (this.loadUnitSlots.truck) {
      this.loadUnitSlots.truck.cargoLoad = new CargoLoad();
    }
    if (this.loadUnitSlots.trailer) {
      this.loadUnitSlots.trailer.cargoLoad = new CargoLoad();
    }

    // calculate the cargoLoad from the projectLoadunits
    this.projectLoadunits().forEach(plu => {
      if (this.loadUnitSlots.truck && plu.LoadunitId == this.loadUnitSlots.truck?.id) {
        this.loadUnitSlots.truck.cargoLoad.push(plu);
      } else if (this.loadUnitSlots.trailer && plu.LoadunitId == this.loadUnitSlots.trailer?.id) {
        this.loadUnitSlots.trailer.cargoLoad.push(plu);
      } else console.error('unmatched loadunit', this.loadUnitSlots, plu);
    });
  }

  // The planning step is done, distribute the load to the loadunits
  async executeCurrentPlanning() {
    if (this.planningExecution.busy()) {
      console.warn('planningBlocked');
      return;
    }

    if (!this.currentPlanning) {
      console.warn('no current planning');
      return;
    }
    // Re-planning removes the existing row first. deleteProjectLoadUnit silently skips while
    // another delete is running, so re-creating the row afterwards would duplicate it.
    if (this.currentPlanning.projectLoadunit && this.deletingRows.anyBusy()) {
      notify(
        { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.DELETE_IN_PROGRESS'), shading: false },
        'warning',
        3000
      );
      return;
    }
    await this.planningExecution.run(async () => {
      try {
        // if a projectLoadunit is present, remove it before re-creating it. For a PL main
        // this must NOT cascade-delete the stations (they are kept and only m is re-planned).
        if (this.currentPlanning!.projectLoadunit) {
          await this.deleteProjectLoadUnit(this.currentPlanning!.projectLoadunit, false, false);
        }
        const position = this.currentPlanning!.position;
        if (this.loadUnitSlotSelectionOrder.length < 2) {
          await this.createProjectLoadUnitsLegacy(position);
        } else {
          await this.createProjectLoadUnitsInOrder(this.currentPlanning!, position);
        }
        await this.fetchProjectLoadunits(); // refresh
        this.planningHelperService.notifyPlanningStepDone(
          {
            AssignmentNo: this.currentPlanning!.assignmentNo!,
            Type: this.currentPlanning!.assignmentType!,
          },
          'done'
        );
        this.checkDataChanged();
      } finally {
        // close the (potentially open) planning dialog
        this.showPlanningDialog = false;
        // reset the current planning
        this.currentPlanning = undefined;
      }
    });
  }

  private async createProjectLoadUnitsInOrder(currentPlanning: PlanningLoadunits, position: number) {
    // when filling, always perserve the loading unit selection order
    for (const loadInputSlotId of this.loadUnitSlotSelectionOrder) {
      const isTruck = this.loadUnitSlots.truck && this.loadUnitSlots.truck.id == loadInputSlotId;
      const planningLoadUnit = isTruck ? currentPlanning.truck : currentPlanning.trailer;
      if (
        await this.createProjectLoadUnit(
          planningLoadUnit,
          currentPlanning.assignmentNo,
          currentPlanning.assignmentType,
          currentPlanning.isFullyAssigned(),
          position,
          false
        )
      ) {
        position++; // increment the position
      }
    }
  }

  private async createProjectLoadUnitsLegacy(position: number) {
    if (
      await this.createProjectLoadUnit(
        this.currentPlanning!.truck, // this.loadUnitSlots.truck.id,
        this.currentPlanning!.assignmentNo,
        this.currentPlanning!.assignmentType,
        this.currentPlanning!.isFullyAssigned(),
        position,
        false
      )
    ) {
      position++; // increment the position for the trailer
    }
    // if there's planned weight or volume for the trailer, create a new projectLoadunit for it
    await this.createProjectLoadUnit(
      this.currentPlanning!.trailer,
      this.currentPlanning!.assignmentNo,
      this.currentPlanning!.assignmentType,
      this.currentPlanning!.isFullyAssigned(),
      position,
      false
    );
  }

  async createProjectLoadUnit(
    planningLoadUnit: PlanningLoadunit,
    assignmentNo: number,
    type: string,
    fullyAssigned: boolean,
    position: number,
    refreshProjectLoadUnits: boolean = true
  ): Promise<boolean> {
    if (!planningLoadUnit) return false;
    const weight = planningLoadUnit.plannedWeight;
    const volume = planningLoadUnit.plannedVolume;
    if (weight <= 0 || volume <= 0) {
      return false;
    }
    try {
      const projectNo: number = this.project()().ProjectNo;
      await firstValueFrom(
        this.projectService.addProjectLoadunit(projectNo, {
          projectNo: projectNo,
          assignmentNo: assignmentNo,
          type: type,
          loadunitId: planningLoadUnit.loadUnitSlot!.id, // loadunitId,
          position: position,
          weight: weight,
          volume: volume,
          transportText: '',
          fullyAssigned: fullyAssigned,
        } as AddProjectLoadunitDto)
      );
      if (refreshProjectLoadUnits) {
        await this.fetchProjectLoadunits();
      }
    } catch (error) {
      // console.error('Error during createProjectLoadUnit:', error);
      notify({ message: (error as { message: string }).message, shading: false }, 'error', 3000);
      this.planningHelperService.abortPlanning({
        index: {
          AssignmentNo: this.currentPlanning?.assignmentNo ?? -1,
          Type: this.currentPlanning?.assignmentType ?? 'AssignmentDelivery',
        },
        msg: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.ERROR_CREATING'),
      });
      return false;
    }
    return true;
  }

  async deleteProjectLoadUnit(
    projectLoadUnit: ProjectLoadunitDto,
    refreshProjectLoadUnits: boolean = true,
    cascadePlGroup: boolean = true
  ) {
    // Guard against a double-click on remove, or a second delete started (e.g. on another
    // row of the same PL group) before this cascade's sequential HTTP calls finish - two
    // concurrent cascades would otherwise compute overlapping station/main lists from the
    // same stale snapshot and race to delete the same rows.
    if (this.deletingRows.anyBusy()) {
      return;
    }
    await this.deletingRows.run(projectLoadUnit.Id ?? -1, () =>
      this.removeProjectLoadUnit(projectLoadUnit, refreshProjectLoadUnits, cascadePlGroup)
    );
  }

  private async removeProjectLoadUnit(
    projectLoadUnit: ProjectLoadunitDto,
    refreshProjectLoadUnits: boolean,
    cascadePlGroup: boolean
  ) {
    try {
      if (projectLoadUnit) {
        const projectNo: number = this.project()().ProjectNo;
        let affectedAssignmentNo = projectLoadUnit.AssignmentNo!;
        // verify if special rules due to post planning are met
        if (
          !(
            (
              projectLoadUnit.Type == AssignmentType.AssignmentPickup ||
              projectLoadUnit.Type == AssignmentType.PickupRentalPurchasingOrder // Issue1434
            ) // fixme BUG?
          ) &&
          !this.dispoPlanning()
        ) {
          notify(
            { message: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PROJECT_PLANNED'), shading: false },
            'error',
            3000
          );
          return;
        }
        if (projectLoadUnit.IsPrimeLogistic == true && cascadePlGroup) {
          // Prime logistic: deleting either the main assignment or any station must
          // remove the WHOLE group (main assignment + all its stations) - and because
          // 'm' can appear in multiple load units of a project, ALL ProjectLoadunit
          // rows of the group must be removed (not just one).
          const mainAssignmentNo =
            projectLoadUnit.IsPrimeLogisticStation == true
              ? projectLoadUnit.ParentAssignmentNo!
              : projectLoadUnit.AssignmentNo!;
          affectedAssignmentNo = mainAssignmentNo;

          // All station rows of this main assignment (across all load units).
          const relatedPLStationLoadunits = this.projectLoadunits().filter(
            plu => plu.ParentAssignmentNo == mainAssignmentNo && plu.IsPrimeLogisticStation == true
          );

          // All main assignment rows (across all load units).
          const relatedMainLoadunits = this.projectLoadunits().filter(
            plu => plu.AssignmentNo == mainAssignmentNo && this.isPrimeLogisticMain(plu)
          );

          // Delete all stations first. If any station delete fails, the thrown error
          // aborts the cascade (caught below) and the main assignment is NOT deleted.
          for (const station of relatedPLStationLoadunits) {
            await firstValueFrom(this.projectService.removeProjectLoadunit(projectNo, station.Id!));
          }

          // All stations removed successfully -> now delete every main assignment row.
          for (const main of relatedMainLoadunits) {
            await firstValueFrom(this.projectService.removeProjectLoadunit(projectNo, main.Id!));
          }
        } else if (projectLoadUnit.IsPrimeLogistic == true && !cascadePlGroup) {
          // Internal re-plan cleanup (e.g. redistributing m's load across load units):
          // remove ONLY the main assignment's own load-unit rows and KEEP the stations,
          // since the subsequent re-plan re-creates m's rows but not the stations.
          const mainAssignmentNo = projectLoadUnit.AssignmentNo!;
          affectedAssignmentNo = mainAssignmentNo;

          const relatedMainLoadunits = this.projectLoadunits().filter(
            plu => plu.AssignmentNo == mainAssignmentNo && this.isPrimeLogisticMain(plu)
          );

          for (const main of relatedMainLoadunits) {
            await firstValueFrom(this.projectService.removeProjectLoadunit(projectNo, main.Id!));
          }
        } else {
          // "normal" loadunit, just remove it
          await firstValueFrom(this.projectService.removeProjectLoadunit(projectNo, projectLoadUnit.Id!));
        }
        // Only announce "this step is done" once the caller considers the operation actually
        // finished (refreshProjectLoadUnits=false means more mutations follow, e.g.
        // executeCurrentPlanning's delete-then-recreate) - otherwise other views (e.g. the
        // assignment list's 'planning-removed' subscriber) react to a transient intermediate
        // state instead of the final one.
        if (refreshProjectLoadUnits) {
          await this.fetchProjectLoadunits(); // refresh
          this.planningHelperService.notifyPlanningStepDone(
            {
              AssignmentNo: affectedAssignmentNo,
              Type: projectLoadUnit.Type,
            },
            'removed'
          );
          this.checkDataChanged();
        }
        return;
      }
    } catch (error) {
      console.error('Error during deleteProjectLoadUnit:', error);
      notify({ message: (error as { message: string }).message, shading: false }, 'error', 3000); // TODO: remove this or replace with a more user friendly message
      // A cascade (e.g. prime logistic group) may have partially completed before failing.
      // Refresh to reflect which loadunits were actually removed.
      await this.fetchProjectLoadunits();
    }
  }

  /**
   * Sets a native "Verschieben" tooltip on the DevExtreme reorder handles (the grip icon
   * where the cursor turns into a move cursor). The handle elements are rendered by
   * dx-list, so the title is applied to the DOM after the list content is ready.
   */
  onPlanningListContentReady(event: ContentReadyEvent): void {
    const root = event.element as HTMLElement | undefined;
    if (!root) {
      return;
    }
    const moveTooltip = this.translateService.instant('PROJECT_PLANNING.TOOLTIP.MOVE');
    root.querySelectorAll('.dx-list-reorder-handle').forEach(handle => {
      handle.setAttribute('title', moveTooltip);
    });
  }

  checkDataChanged() {
    this.reloadDataRequired.emit();
  }

  updateProjectLoadunitOrder = async (e: ItemReorderedEvent) => {
    // The list renders displayProjectLoadunits(), which is projectLoadunits() sorted with
    // main-PL rows on top. Each displayed row maps 1:1 to a real underlying row, so we can
    // rebuild the Sorting directly from the new display order.
    const fromIndex = e.fromIndex;
    const toIndex = e.toIndex;
    if (fromIndex === toIndex) {
      return;
    }

    // 1) Compute the new display order after the move.
    const display = [...this.displayProjectLoadunits()];
    const [moved] = display.splice(fromIndex, 1);
    display.splice(toIndex, 0, moved);

    // 2) Assign a fresh, contiguous Sorting following the new display order.
    const changed: ProjectLoadunitDto[] = [];
    let sorting = 1;

    for (const row of display) {
      if (row.Sorting !== sorting) {
        row.Sorting = sorting;
        changed.push(row);
      }
      sorting++;
    }

    // 3) Persist all rows whose Sorting changed. Reordering is disabled while this runs
    //    (see isPlanningAllowed()), so a second, overlapping reorder cannot start.
    await this.loadunitUpdate.run(async () => {
      try {
        for (const row of changed) {
          await firstValueFrom(this.projectService.updateProjectLoadunit(this.project()().ProjectNo, row));
        }
      } catch (error) {
        console.error('Error during updateProjectLoadunitOrder:', error);
        notify({ message: (error as { message: string }).message, shading: false }, 'error', 3000);
      } finally {
        await this.fetchProjectLoadunits();
      }
    });
  };

  // create a new planning object and open the dialog
  openPlanningDialog(assignment: AssignmentDto, position: number) {
    this.currentPlanning = new PlanningLoadunits(this.loadUnitSlots, position);
    // this.currentPlanning = new PlanningLoadunit(this.loadUnitSlots, position);
    this.currentPlanning.setAssignment(assignment);
    this.currentPlanning?.fillTruck();
    this.currentPlanning?.fillTrailer();
    this.showPlanningDialog = true;
  }

  // the projectLoadunit already exists, reopen the dialog
  reOpenPlanningDialog(projectLoadunit: ProjectLoadunitDto) {
    this.currentPlanning = new PlanningLoadunits(this.loadUnitSlots, projectLoadunit.Sorting!);
    this.currentPlanning.setProjectLoadunit(projectLoadunit);
    this.showPlanningDialog = true;
  }

  // create a new planning object without opening the dialog
  distributeWithoutDialog(assignment: AssignmentDto, position: number) {
    // this.currentPlanning = new PlanningLoadunit(this.loadUnitSlots, position);
    this.currentPlanning = new PlanningLoadunits(this.loadUnitSlots, position);
    this.currentPlanning.setAssignment(assignment);
  }

  closeChangeLoadunitDialog = () => {
    // If the dialog was closed without selecting a loadunit, the planning may not be finished
    if (this.loadUnitSlots.truck && this.currentPlanning) {
      this.showPlanningDialog = true;
    } else this.currentPlanning = undefined;

    this.showChangeLoadunitDialog = false;
  };

  closePlStationSelectionDialog = () => {
    if (this.plStationSelectionResolved) {
      this.showPlStationSelectionDialog = false;
      return;
    }
    this.plStationSelectionResolved = true;
    this.showPlStationSelectionDialog = false;
    this.onUserPlStationSelectionAvailable.next(null);
    this.planningHelperService.abortPlanning({
      index: {
        AssignmentNo: this.currentPlAssignment?.AssignmentNo ?? -1,
        Type: this.currentPlAssignment?.Type ?? 'AssignmentDelivery',
      },
      msg: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'),
    });
  };

  @HostListener('document:keydown', ['$event']) assurePlanningDiscontinued(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.anyPopUpVisible()) {
        this.onRestRemains();
      }
    }
  }

  async onRestRemains() {
    this.showChangeLoadunitDialog = false;
    this.onUserLoadunitSelectionAvailable.next(new LoadunitUserSelection(-1, true));
  }

  anyPopUpVisible() {
    return (
      this.showChangeLoadunitDialog ||
      this.showPlanningDialog ||
      this.showAssignmentStatusDialog ||
      this.showPlStationSelectionDialog
    );
  }

  closePlanningDialog = () => {
    this.showPlanningDialog = false;
    this.fetchProjectLoadunits();
    this.calculateCargoLoad();
    this.planningHelperService.abortPlanning({
      index: {
        AssignmentNo: this.currentPlanning?.assignmentNo ?? -1,
        Type: this.currentPlanning?.assignmentType ?? 'AssignmentDelivery',
      },
      msg: this.translateService.instant('PROJECT_PLANNING.NOTIFICATION.PLANNING_ABORTED'),
    });
    this.currentPlanning = undefined;
  };

  openAssignmentStatusDialog(plu: ProjectLoadunitDto) {
    this.assignmentCompleteData = {
      ProjectLoadunitId: plu.Id!,
      Completed: plu.Completed,
      CompletionOption: this.assignmentCompleteOptions[0].value,
      Subject: '',
      Comment: '',
      CompletionOptionsList: [],
    };

    this.showAssignmentStatusDialog = true;
  }

  sendAssignmentStatusChange = async () => {
    const enumKey = this.assignmentCompleteData.CompletionOption;
    this.showAssignmentStatusDialog = false;
    if (this.assignmentCompleteData) {
      // take a reference, reopenAssignment() replaces assignmentCompleteData for the next row
      const completeData = this.assignmentCompleteData;
      completeData.CompletionOptionsList = this.assignmentCompleteOptions.map(opt => opt.display);
      completeData.Subject = this.translateService.instant(`ASSIGNMENT_COMPLETION_OPTION.${enumKey}`);

      await this.rowBusy.run(completeData.ProjectLoadunitId, async () => {
        try {
          await firstValueFrom(this.assignmentService.completeAssignment(completeData));
        } catch (err) {
          console.error('Error during sendAssignmentStatusChange:', err);
          let message: string = 'AssignmentError.Unknown';
          if (err && typeof err === 'object' && 'error' in err && typeof err.error === 'string') {
            message = 'AssignmentError.' + err.error;
          }
          notify({ message: this.translateService.instant(message), shading: false }, 'error', 3000);
        }
        await this.fetchProjectLoadunits();
      });
    }
  };

  reopenAssignment = async (plu: ProjectLoadunitDto) => {
    if (this.rowBusy.isBusy(plu.Id!)) return;
    this.assignmentCompleteData = {
      ProjectLoadunitId: plu.Id!,
      Completed: false,
      CompletionOption: this.assignmentCompleteOptions[0].value,
      Subject: this.translateService.instant(
        `ASSIGNMENTDETAILS.ASSIGNMENT_COMPLETION_OPTION.${this.assignmentCompleteOptions[0].value}`
      ),
      Comment: '',
    };
    await this.sendAssignmentStatusChange();
  };

  finishText(): string {
    return this.currentPlanning?.truck.overload() || this.currentPlanning?.trailer.overload()
      ? 'PROJECT_PLANNING.FINISH_OVERLOAD'
      : 'PROJECT_PLANNING.FINISH';
  }

  getEventDateDisplay(plu: ProjectLoadunitDto) {
    let dateTimeString: string = '';
    if (plu.EventDate) {
      dateTimeString = formatDateAndTime(plu.EventDate, showEventTime(plu) ? plu.EventTime : undefined);
    }
    return dateTimeString;

    function formatDateAndTime(dateString: string, timeString: string | undefined): string {
      try {
        if (!timeString) {
          return new Date(dateString).toLocaleDateString('de-DE');
        }

        // Find the index of 'T' in the date string
        const tIndex = dateString.indexOf('T');

        // Extract the date part (before 'T')
        const datePart = dateString.substring(0, tIndex);

        // Ensure the time string is in HH:mm format (pad single-digit hours)
        if (timeString.indexOf(':') !== -1) {
          const [hour, minute] = timeString.split(':').map(Number);
          // if (hour == 0 && minute == 0) return datePart;
          const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          // Construct the new datetime string
          return new Date(`${datePart}T${formattedTime}`).toLocaleString('de-DE');
        }
        return `${new Date(datePart).toLocaleDateString('de-DE')} ${timeString}`;
      } catch {
        // console.error('Error during replaceTimeInDate:', error);
        return '';
      }
    }
  }

  isPlStationSelectable = (station: AssignmentDto): boolean => {
    // A station can be selected (and is selected by default) unless it is fully
    // assigned (its planned amount reached the main assignment's available amount).
    // Requirement 7.1: a fully-assigned station must not be planned again. Stations
    // that are only partially planned (assigned to some project but not fully) remain
    // selectable and are selected by default.
    return station.StationFullyAssigned !== true;
  };
}
